// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import {
  IBranchGetListAttrs,
  IBranchGetDetailAttrs,
  IBranchGetDropdownAttrs,
  IBranchSaveAttrs,
} from 'interfaces/back-office/master/branch/branch-view.interface'
import {
  IBranchApiDetailResponse,
  IBranchApiDropdownResponse,
  IBranchApiListResponse,
  IBranchApiNullResponse,
} from 'interfaces/back-office/master/branch/branch-api.interface'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponseDropdownTypesModel, withHelper_paginationProps } from 'models/extensions/with-helper'

// Lodash
import omit from 'lodash/omit'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Constant
import { LOADING } from 'constant/shared.constant'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'

// Types
const { model, maybeNull, number, string, boolean, optional, array } = types

// Loading
const { BASE, DETAIL, ACTION, DROPDOWN } = LOADING

// Branch Props
const branchProps = {
  number: maybeNull(number),
  id: maybeNull(number),
  name: maybeNull(string),
  address: maybeNull(string),
  companyId: maybeNull(number),
  companyName: maybeNull(string),
  phoneNumber: maybeNull(string),
  fax: maybeNull(string),
  postalCode: maybeNull(string),
  status: maybeNull(boolean),
  createdAt: maybeNull(string),
  updatedAt: maybeNull(string),
}

// Branch List Model
const BranchList = optional(array(model('BranchList').props(omit(branchProps, ['companyId']))), [])

// Branch Paginated Model
const BranchPaginated = model('BranchPaginated').props({ ...withHelper_paginationProps, list: BranchList })

// Branch Detail Model
const BranchDetail = model('BranchDetail').props(branchProps)

// Base Model
const BranchStoreModel = types
  .model('Branch')
  .props({
    masterBranch_loading: withHelper_commonLoadingModel,
    masterBranch_list: optional(BranchPaginated, {}),
    masterBranch_detail: optional(BranchDetail, {}),
    masterBranch_dropdown: maybeNull(withHelper_baseResponseDropdownTypesModel('BranchDropdown')),
  })
  .actions(self => ({
    masterBranch_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.masterBranch_loading, payload)

      detach(self.masterBranch_loading)
      self.masterBranch_loading = cast(map)
    },
    masterBranch_SET_LIST: (payload: IBranchApiListResponse['res']['data']): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.masterBranch_list = cast(mapPayload)
    },
    masterBranch_SET_DETAIL: (payload: IBranchApiDetailResponse['res']['data']): void => {
      self.masterBranch_detail = cast(payload)
    },
    masterBranch_SET_DROPDOWN: (payload: IBranchApiDropdownResponse['res']['data']): void => {
      self.masterBranch_dropdown = cast(payload.filter(dropdown => dropdown.value))
    },
    masterBranch_CLEAR_LIST: (): void => {
      self.masterBranch_list = cast({})
    },
    masterBranch_CLEAR_DETAIL: (): void => {
      self.masterBranch_detail = cast({})
    },
    masterBranch_CLEAR_DROPDOWN: (): void => {
      self.masterBranch_dropdown = cast([])
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch list
     *
     * @param {IBranchGetListAttrs} payload
     *
     * @return {any} any
     */
    masterBranch_fetchList: flow(function* (payload: IBranchGetListAttrs) {
      self.masterBranch_SET_LOADING({ type: BASE, value: true })

      const response: IBranchApiListResponse = yield self.environment.masterBranchApi.masterBranch_getList({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.masterBranch_SET_LIST(response.res.data)

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.masterBranch_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Fetch detail branch
     *
     * @param {IBranchGetDetailAttrs} payload
     *
     * @return {any} any
     */
    masterBranch_fetchDetail: flow(function* (payload: IBranchGetDetailAttrs) {
      self.masterBranch_SET_LOADING({ type: DETAIL, value: true })

      const response: IBranchApiDetailResponse = yield self.environment.masterBranchApi.masterBranch_getDetail({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.masterBranch_SET_DETAIL(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.masterBranch_SET_LOADING({ type: DETAIL, value: false })
    }),

    /**
     * @description Save branch
     *
     * @param {IBranchSaveAttrs} payload
     *
     * @return {any} any
     */
    masterBranch_save: flow(function* (payload: IBranchSaveAttrs) {
      self.masterBranch_SET_LOADING({ type: ACTION, value: true })

      const response: IBranchApiNullResponse = yield self.environment.masterBranchApi.masterBranch_save({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.masterBranch_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Fetch branch dropdown
     *
     * @param {IBranchGetDropdownAttrs} payload
     *
     * @return {any} any
     */
    masterBranch_fetchDropdown: flow(function* (payload: IBranchGetDropdownAttrs) {
      self.masterBranch_SET_LOADING({ type: DROPDOWN, value: true })

      const response: IBranchApiDropdownResponse = yield self.environment.masterBranchApi.masterBranch_getDropdown({
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.masterBranch_SET_DROPDOWN(response.res.data || [])

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.masterBranch_SET_LOADING({ type: DROPDOWN, value: false })
    }),
  }))

export const createBranchStoreDefaultModel = () => optional(BranchStoreModel, {})
